// src/pages/modules/InventoryPage.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchInventories,
  fetchInventoryById,
  createInventory,
  updateInventory,
  deleteInventory,
  clearError as clearInventoryError,
  clearSelectedItem,
  exportInventories,
  importPreview,
  importConfirm,
} from "../../store/slices/inventorySlice";
import {
  fetchCategories,
  clearError as clearCategoryError,
} from "../../store/slices/categorySlice";
import {
  fetchSubcategories,
  clearError as clearSubcategoryError,
} from "../../store/slices/subcategorySlice";
import { Pencil, Trash2, MoreHorizontal, Eye, Loader2, Snowflake, SunSnow, MoreVertical } from "lucide-react";
import DataTable from "../../components/common/DataTable";
import PageHeader from "../../components/common/PageHeader";
import FormField from "../../components/common/FormField";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import inventoryApi from "../../api/inventoryApi";
import { usePermissions } from "../../hooks/usePermissions";
import usePermissionSocket from "../../hooks/usePermissionSocket";
import { fetchInventoryCategories, fetchInventorySubcategories, restoreRole } from "store/slices/commonSlice";
import { fetchVendors } from "store/slices/vendorSlice";
import { formatDateTime } from "utlis/formateDateTime";
import { cn } from "lib/utils";

const InventoryPage = () => {
  usePermissionSocket();

  const dispatch = useDispatch();

  const {
    items,
    selectedItem,
    pagination,
    isLoading,
    isLoadingDetails,
    error: invError,
  } = useSelector((s) => s.inventory);


  // const { categories, isLoading: catLoading } = useSelector((s) => s.category);
  // const { subcategories, isLoading: subLoading } = useSelector(
  //   (s) => s.subcategory
  // );

  const {
    inventoryCategories,
    inventorySubcategories,
  } = useSelector((state) => state.common);

  const categories = inventoryCategories.items;
  const catLoading = inventoryCategories.loading;

  const { vendors } = useSelector((state) => state.vendor);

  const { canCreate, canUpdate, canDelete, canExport, canImport, canRead } =
    usePermissions("inventory");

  // Pagination & Search
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDuplicateConfirmOpen, setIsDuplicateConfirmOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [frozenItems, setFrozenItems] = useState(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    category: "",
    sub_category: "",
    quantity: "",
    amount: "",
    brand_name: "",
    model_no: "",
    serial_no: "",
    purchased_on: "",
    warranty_expired_on: "",
    vendor: "",
    invoice_no: "",
    last_purchased_on: null,
  });
  const [formErrors, setFormErrors] = useState({});

  const subcategories = formData.category
    ? inventorySubcategories.byCategory[formData.category]?.items || []
    : [];
  // const subLoading = formData.category
  //   ? inventorySubcategories.byCategory[formData.category]?.loading || false
  //   : false;
  // Dispatch on mount
  useEffect(() => {
    dispatch(fetchInventoryCategories());
    dispatch(fetchVendors({ page: 1, pageSize: 10000, is_active: true }));
  }, [dispatch]);

  // Fetch subcategories when category changes
  useEffect(() => {
    if (formData.category) {
      dispatch(fetchInventorySubcategories(formData.category));
    }
  }, [formData.category, dispatch]);


  // Reset page when searching
  useEffect(() => {
    const timer = setTimeout(() => setPage(1), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Refetch with search + pagination
  const refetch = useCallback(() => {
    if (canRead) {
      dispatch(fetchInventories({ page, pageSize, search: searchTerm }));
    }
  }, [dispatch, page, pageSize, searchTerm, canRead]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Load categories & subcategories once (no pagination)
  // useEffect(() => {
  //   if (canRead) {
  //     dispatch(fetchCategories({ page: 1, pageSize: 100 }));
  //     dispatch(fetchSubcategories({ page: 1, pageSize: 100 })); // Loads ALL subcategories at once
  //   }
  // }, [dispatch, canRead]);

  // Error toast
  useEffect(() => {
    if (invError) {
      toast.error(typeof invError === "string" ? invError : "Operation failed");
      dispatch(clearInventoryError());
    }
  }, [invError, dispatch]);

  // CORRECT filtering — category is UUID string, not int
  // const filteredSubcategories = useMemo(() => {
  //   if (!formData.category) return [];
  //   return subcategories.filter((s) => s.category === formData.category);
  // }, [formData.category, subcategories]);

  // CORRECT: Use the already-fetched subcategories directly from Redux
  const currentSubcategories = useMemo(() => {
    if (!formData.category) return [];
    const categoryData = inventorySubcategories.byCategory[formData.category];
    return categoryData?.items || [];
  }, [formData.category, inventorySubcategories.byCategory]);

  const subLoading = useMemo(() => {
    if (!formData.category) return false;
    const categoryData = inventorySubcategories.byCategory[formData.category];
    return categoryData?.loading || false;
  }, [formData.category, inventorySubcategories.byCategory]);

  // Auto-fill last purchased date
  useEffect(() => {
    if (formData.sub_category && !currentItem && canRead) {
      const fetchLatest = async () => {
        try {
          const res = await inventoryApi.getAll(
            1,
            100,
            formData.sub_category,
            "-purchased_on"
          );
          const latest = res.data?.[0];
          setFormData((prev) => ({
            ...prev,
            last_purchased_on: latest?.purchased_on || null,
          }));
        } catch (err) {
          console.error(err);
        }
      };
      fetchLatest();
    }
  }, [formData.sub_category, currentItem, canRead]);

  const validateForm = () => {
    const e = {};
    if (!formData.category) e.category = "Required";
    if (!formData.sub_category) e.sub_category = "Required";
    if (!formData.quantity || formData.quantity <= 0)
      e.quantity = "Must be > 0";
    if (!formData.purchased_on) e.purchased_on = "Required";

    const selectedCategory = categories.find(c => c.id === formData.category);
    const isITCategory = selectedCategory?.is_it === true;

    if (isITCategory && !formData.serial_no?.trim()) {
      e.serial_no = "Serial number is required for IT items.";
    }

    setFormErrors(e);
    return Object.keys(e).length === 0;
  };

  const openEditDialog = (item = null) => {
    if (item && !canUpdate) return toast.error("No edit permission");
    if (!item && !canCreate) return toast.error("No create permission");

    setCurrentItem(item);
    setFormData(
      item
        ? {
          category: item.category,
          sub_category: item.sub_category,
          quantity: item.quantity,
          amount: item.amount || "",
          brand_name: item.brand_name || "",
          model_no: item.model_no || "",
          serial_no: item.serial_no || "",
          purchased_on: item.purchased_on,
          warranty_expired_on: item.warranty_expired_on || "",
          vendor: item.vendor || "",
          invoice_no: item.invoice_no || "",
          last_purchased_on: item.last_purchased_on || null,
        }
        : {
          category: "",
          sub_category: "",
          quantity: "",
          amount: "",
          brand_name: "",
          model_no: "",
          serial_no: "",
          purchased_on: "",
          warranty_expired_on: "",
          vendor: "",
          invoice_no: "",
          last_purchased_on: null,
        }
    );
    setFormErrors({});
    setIsDialogOpen(true);
  };

  const openViewDialog = async (id) => {
    if (!canRead) return toast.error("No view permission");
    dispatch(clearSelectedItem());
    setIsViewDialogOpen(true);
    await dispatch(fetchInventoryById(id));
  };

  const findDuplicate = () => {
    return items.find((i) => {
      if (currentItem && i.id === currentItem.id) return false;
      return (
        i.category === formData.category &&
        i.sub_category === formData.sub_category &&
        i.purchased_on === formData.purchased_on &&
        (i.brand_name || "").trim() === (formData.brand_name || "").trim() &&
        (i.model_no || "").trim() === (formData.model_no || "").trim() &&
        (i.serial_no || "").trim() === (formData.serial_no || "").trim()
      );
    });
  };

  const performSubmit = async () => {
    setIsSubmitting(true);
    const payload = {
      category: formData.category,
      sub_category: formData.sub_category,
      quantity: Number(formData.quantity),
      amount: Number(formData.amount) || 0,
      purchased_on: formData.purchased_on,
      ...(formData.brand_name && { brand_name: formData.brand_name.trim() }),
      ...(formData.model_no && { model_no: formData.model_no.trim() }),
      ...(formData.serial_no && { serial_no: formData.serial_no.trim() }),
      ...(formData.warranty_expired_on && {
        warranty_expired_on: formData.warranty_expired_on,
      }),
      ...(formData.vendor && { vendor: formData.vendor.trim() }),
      ...(formData.invoice_no && { invoice_no: formData.invoice_no.trim() }),
      ...(formData.last_purchased_on &&
        !currentItem && { last_purchased_on: formData.last_purchased_on }),
    };

    try {
      if (currentItem) {
        await dispatch(
          updateInventory({ id: currentItem.id, data: payload })
        ).unwrap();
        toast.success("Item updated");
      } else {
        await dispatch(createInventory(payload)).unwrap();
        toast.success("Item added");
      }
      refetch();
      setIsDialogOpen(false);
    }
    catch (err) {
      console.error("Full submit error:", err);
      let serverErrors = {};
      let displayMessage = "Failed to save item. Please try again.";

      const possibleErrorData =
        err?.data ||
        err?.payload?.data ||
        err?.error?.data ||
        err?.response?.data ||     // raw axios response
        err?.detail ||
        err;

      if (possibleErrorData && typeof possibleErrorData === 'object' && !Array.isArray(possibleErrorData)) {
        // Standard DRF validation format
        Object.entries(possibleErrorData).forEach(([field, messages]) => {
          if (Array.isArray(messages) && messages.length > 0) {
            serverErrors[field] = messages[0];
          } else if (typeof messages === 'string') {
            serverErrors[field] = messages;
          }
        });

        if (Object.keys(serverErrors).length > 0) {
          const firstMsg = Object.values(serverErrors)[0];
          displayMessage = firstMsg;
        } else if (possibleErrorData.detail || possibleErrorData.non_field_errors) {
          displayMessage = possibleErrorData.detail || possibleErrorData.non_field_errors?.[0] || displayMessage;
        }
      } else if (err?.message) {
        displayMessage = err.message;
      }
      if (Object.keys(serverErrors).length > 0) {
        setFormErrors((prev) => ({ ...prev, ...serverErrors }));
      }
      toast.error(displayMessage);

      const firstInvalidField = Object.keys(serverErrors)[0];
      if (firstInvalidField) {
        document.querySelector(`input[name="${firstInvalidField}"]`)?.focus();
      }
    }
    finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!validateForm()) return;

    const duplicate = findDuplicate();
    if (duplicate) {
      setIsDuplicateConfirmOpen(true);
      return;
    }
    await performSubmit();
  };

  const confirmDuplicate = async () => {
    setIsDuplicateConfirmOpen(false);
    await performSubmit();
  };

  const confirmDelete = async () => {
    if (!canDelete) return toast.error("No delete permission");
    await dispatch(deleteInventory(currentItem.id)).unwrap();
    toast.success("Item deleted");
    refetch();
    setIsDeleteDialogOpen(false);
  };

  const toggleFreeze = (id) => {
    setFrozenItems((prev) => {
      const updated = new Set(prev);
      updated.has(id) ? updated.delete(id) : updated.add(id);
      return updated;
    });
  };

  const getCategoryName = (id) =>
    categories.find((c) => c.id === id)?.name || "—";
  const getSubcategoryName = (id) =>
    subcategories.find((s) => s.id === id)?.name || "—";

  const columns = [
    {
      header: "Items",
      cell: ({ row }) => (
        <div>
          <p>
            {row.original.sub_category_name ?? '-'}
          </p>
          <p className="text-xs text-muted-foreground">{row.original.category_name ?? '-'}</p>
        </div>
      ),
    },
    {
      header: "Qty",
      cell: ({ row }) => (
        <Badge variant="outline">{row.original.quantity}</Badge>
      ),
    },
    {
      header: "Purchased On",
      cell: ({ row }) => (
        <div className="text-sm">
          <p>
            {new Date(row.original.purchased_on).toLocaleDateString("en-IN")}
          </p>
          <p className="text-xs text-muted-foreground">
            Inv: {row.original.invoice_no || "—"}
          </p>
        </div>
      ),
    },
    {
      header: "Brand",
      cell: ({ row }) => (
        <span className="font-medium">{row.original.brand_name || "—"}</span>
      ),
    },
    {
      header: "Model / Serial",
      cell: ({ row }) => (
        <div>
          <p className="font-medium">{row.original.model_no || "—"}</p>
          <p className="text-xs text-muted-foreground">
            SN: {row.original.serial_no || "—"}
          </p>
        </div>
      ),
    },



    {
      header: "Warranty",
      cell: ({ row }) =>
        row.original.warranty_expired_on ? (
          <Badge
            variant={
              new Date(row.original.warranty_expired_on) < new Date()
                ? "destructive"
                : "secondary"
            }
          >
            {new Date(row.original.warranty_expired_on).toLocaleDateString(
              "en-IN"
            )}
          </Badge>
        ) : (
          "—"
        ),
    },
    {
      header: "Last Purchased On",
      cell: ({ row }) =>

        (formatDateTime(row.original.last_purchased_on))
    },
    {
      accessorKey: 'created_at',
      header: 'Created On',
      cell: ({ row }) => <span className="text-sm">{formatDateTime(row.original.created_at) ?? '-'}</span>,
    },
    {
      accessorKey: 'created_by_name',
      header: 'Created By',
      cell: ({ row }) => <span className="text-sm">{row.original.created_by_name ?? '-'}</span>,
    },
    {
      id: "actions",
      header: 'Actions',
      cell: ({ row }) => {
        const item = row.original;
        const isFrozen = frozenItems.has(item.id);
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canRead && (
                <DropdownMenuItem onClick={() => openViewDialog(item.id)}>
                  <Eye className="mr-2 h-4 w-4" /> View
                </DropdownMenuItem>
              )}
              {canUpdate && (
                <DropdownMenuItem
                  onClick={() => !isFrozen && openEditDialog(item)}
                  disabled={isFrozen}
                >
                  <Pencil className="mr-2 h-4 w-4" /> Edit
                </DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem
                  onClick={() =>
                    !isFrozen &&
                    (setCurrentItem(item), setIsDeleteDialogOpen(true))
                  }
                  disabled={isFrozen}
                  className="text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </DropdownMenuItem>
              )}
              {/* {canUpdate && (
                <DropdownMenuItem onClick={() => toggleFreeze(item.id)}>
                  {isFrozen ? <SunSnow className="mr-2 h-4 w-4 text-orange-500" /> : <Snowflake className="mr-2 h-4 w-4 text-blue-500" />} {isFrozen ? "Unfreeze" : "Freeze"}
                </DropdownMenuItem>
              )} */}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const handleImportPreview = async (file) => {
    if (!canImport) throw new Error("No import permission");
    return await dispatch(importPreview(file)).unwrap();
  };

  const handleImportConfirm = async (preview) => {
    if (!canImport) throw new Error("No import permission");
    await dispatch(importConfirm(preview)).unwrap();
    refetch();
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Inventory"
        description="Manage IT and office assets"
        action={
          canCreate
            ? { label: "Add Item", onClick: () => openEditDialog() }
            : null
        }
      />

      {/* {isLoading && items.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin mr-3" />
          <span className="text-lg">Loading inventory...</span>
        </div>
      )} */}

      {/* {!canRead && !isLoading && (
        <div className="text-center py-16 text-muted-foreground text-lg">
          You don't have permission to view inventory.
        </div>
      )} */}

      {canRead && (
        <DataTable
          data={items}
          totalCount={pagination.count}
          columns={columns}
          templateUrl="/import-template/inventory.csv"
          templateDownloadName="inventory-import-template.csv"
          templateKey="inventory"
          searchPlaceholder="Search by brand, model, serial, invoice..."
          showExport={canExport}
          onExport={canExport ? () => dispatch(exportInventories()) : undefined}
          showImport={canImport}
          onImportPreview={canImport ? handleImportPreview : undefined}
          onImportConfirm={canImport ? handleImportConfirm : undefined}
          pageIndex={page - 1}
          pageSize={pageSize}
          pageCount={
            pagination.count
              ? Math.ceil(pagination.count / pagination.page_size)
              : 1
          }
          onPageChange={(i) => setPage(i + 1)}
          onSearch={setSearchTerm}
          isLoading={isLoading}
          onDelete={async (id) => {
            await dispatch(deleteInventory(id));
            refetch();
          }}
          onRestore={async (id) => {
            await dispatch(restoreRole({ entityType: 'inventories', id }));
            refetch();
          }}
        />
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{currentItem ? "Edit" : "Add"} Item</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                label="Category"
                name="category"
                type="select"
                value={formData.category}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    category: e.target.value,
                    sub_category: "",
                  });
                  setFormErrors((prev) => ({ ...prev, sub_category: "" }));
                }}
                error={formErrors.category}
                required
                placeholder={
                  catLoading ? "Loading categories..." : "Select category"
                }
                disabled={catLoading}
                options={categories.map((c) => ({
                  value: c.id,
                  label: c.name,
                }))}
              />
              {/* <FormField
                label="Subcategory"
                name="sub_category"
                type="select"
                value={formData.sub_category}
                onChange={(e) =>
                  setFormData({ ...formData, sub_category: e.target.value })
                }
                error={formErrors.sub_category}
                required
                placeholder={
                  subLoading
                    ? "Loading..."
                    : !formData.category
                      ? "First select category"
                      : filteredSubcategories.length === 0
                        ? "No subcategories"
                        : "Select subcategory"
                }
                disabled={
                  subLoading ||
                  !formData.category ||
                  filteredSubcategories.length === 0
                }
                options={filteredSubcategories.map((s) => ({
                  value: s.id,
                  label: s.name,
                }))}
              /> */}

              <FormField
                label="Subcategory"
                name="sub_category"
                type="select"
                value={formData.sub_category}
                onChange={(e) =>
                  setFormData({ ...formData, sub_category: e.target.value })
                }
                error={formErrors.sub_category}
                required
                placeholder={
                  subLoading
                    ? "Loading..."
                    : !formData.category
                      ? "First select category"
                      : currentSubcategories.length === 0
                        ? "No subcategories found"
                        : "Select subcategory"
                }
                disabled={
                  subLoading ||
                  !formData.category ||
                  currentSubcategories.length === 0
                }
                options={currentSubcategories.map((s) => ({
                  value: s.id,
                  label: s.name,
                }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                label="Quantity"
                name="quantity"
                type="number"
                value={formData.quantity}
                onChange={(e) =>
                  setFormData({ ...formData, quantity: e.target.value })
                }
                error={formErrors.quantity}
                required
                min="1"
              />
              <FormField
                label="Amount"
                name="amount"
                type="number"
                value={formData.amount}
                onChange={(e) =>
                  setFormData({ ...formData, amount: e.target.value })
                }
                error={formErrors.amount}
                min="0"
                step="0.01"
              />
              <FormField
                label="Purchase Date"
                name="purchased_on"
                type="date"
                value={formData.purchased_on}
                max={new Date().toISOString().split("T")[0]}
                onChange={(e) =>
                  setFormData({ ...formData, purchased_on: e.target.value })
                }
                error={formErrors.purchased_on}
                required
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <FormField
                label="Brand"
                name="brand_name"
                value={formData.brand_name}
                onChange={(e) =>
                  setFormData({ ...formData, brand_name: e.target.value })
                }
              />
              <FormField
                label="Model No"
                name="model_no"
                value={formData.model_no}
                onChange={(e) =>
                  setFormData({ ...formData, model_no: e.target.value })
                }
              />
              {/* <FormField
                label="Serial No"
                name="serial_no"
                value={formData.serial_no}
                onChange={(e) =>
                  setFormData({ ...formData, serial_no: e.target.value })
                }
              /> */}
              <div className="space-y-2">
                <FormField
                  label="Serial No"
                  name="serial_no"
                  value={formData.serial_no}
                  onChange={(e) =>
                    setFormData({ ...formData, serial_no: e.target.value })
                  }
                  // ─── NEW: conditional red border when error exists ───
                  className={cn(
                    formErrors.serial_no && "form-error border-destructive focus:ring-destructive/20"
                  )}
                  // Optional: add placeholder hint when IT category
                  placeholder={
                    categories.find(c => c.id === formData.category)?.is_it
                      ? "Required for IT items"
                      : "Optional"
                  }
                />
                {/* ─── NEW: Show error message below field ─── */}
                {formErrors.serial_no && (
                  <p className="text-sm text-destructive mt-1">
                    {formErrors.serial_no}
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <FormField
                label="Warranty Expiry"
                name="warranty_expired_on"
                type="date"
                value={formData.warranty_expired_on}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    warranty_expired_on: e.target.value,
                  })
                }
              />
              <FormField
                id="vendor"
                label="Vendor"
                name="vendor"
                type="select"
                value={formData.vendor}
                onChange={(e) =>
                  setFormData({ ...formData, vendor: e.target.value })
                }
                options={vendors.map((d) => ({
                  value: d.id,
                  label: d.name,
                }))}
              />
              <FormField
                label="Invoice No"
                name="invoice_no"
                value={formData.invoice_no}
                onChange={(e) =>
                  setFormData({ ...formData, invoice_no: e.target.value })
                }
              />
            </div>
            <div className="pt-4 border-t">
              <FormField
                label="Last Purchased On"
                value={
                  formData.last_purchased_on
                    ? new Date(formData.last_purchased_on).toLocaleDateString()
                    : "—"
                }
                readOnly
                disabled
                className="bg-muted"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
              >
                Cancel
              </Button>
              {/* <Button type="submit">
                {currentItem ? "Update" : "Add"} Item
              </Button> */}
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {currentItem ? "Updating..." : "Adding..."}
                  </>
                ) : (
                  <>
                    {currentItem ? "Update" : "Add"} Item
                  </>
                )}
              </Button>

            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Duplicate Confirm */}
      <AlertDialog
        open={isDuplicateConfirmOpen}
        onOpenChange={setIsDuplicateConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Duplicate Detected</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            A similar item already exists. Continue anyway?
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>No</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDuplicate}>
              Yes, Proceed
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Item Details</DialogTitle>
          </DialogHeader>
          {isLoadingDetails ? (
            <div className="flex justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            selectedItem && (
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <strong>Brand:</strong> {selectedItem.brand_name || "—"}
                  </div>
                  <div>
                    <strong>Model:</strong> {selectedItem.model_no || "—"}
                  </div>
                  <div>
                    <strong>Serial:</strong> {selectedItem.serial_no || "—"}
                  </div>
                  <div>
                    <strong>Qty:</strong> {selectedItem.quantity}
                  </div>
                  <div>
                    <strong>Category:</strong>{" "}
                    {getCategoryName(selectedItem.category)}
                  </div>
                  <div>
                    <strong>Subcategory:</strong>{" "}
                    {getSubcategoryName(selectedItem.sub_category)}
                  </div>
                  <div>
                    <strong>Purchase Date:</strong>{" "}
                    {new Date(selectedItem.purchased_on).toLocaleDateString()}
                  </div>
                  <div>
                    <strong>Warranty:</strong>{" "}
                    {selectedItem.warranty_expired_on
                      ? new Date(
                        selectedItem.warranty_expired_on
                      ).toLocaleDateString()
                      : "—"}
                  </div>
                </div>
              </div>
            )
          )}
          <div className="flex justify-end mt-6">
            <Button onClick={() => setIsViewDialogOpen(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Item?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            Delete <strong>{currentItem?.brand_name || "this item"}</strong>?
            This cannot be undone.
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InventoryPage;
